import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Stack,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Lock as LockIcon,
  Shield as ShieldIcon,
  Visibility as VisibilityIcon,
  Security as SecurityIcon,
  VerifiedUser as VerifiedUserIcon,
  CheckCircle as CheckCircleIcon,
  Policy as PolicyIcon,
  Support as SupportIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import SEOErrorBoundary from '../components/SEOErrorBoundary';
import useStructuredData from '../hooks/useStructuredData';
import { useLanguageUrl } from '../utils/languageUtils';

const OurCommitment: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { getTrustPageData } = useStructuredData();
  const getLanguageUrl = useLanguageUrl();

  const pageTitle = t('ourCommitment.title');
  const pageDescription = i18n.language === 'en'
    ? 'Learn about Alerta Legal commitment to privacy and security. We protect your data, operate only on public information, and never sell your personal information.'
    : 'Conoce el compromiso de Alerta Legal con la privacidad y seguridad. Protegemos tus datos, operamos solo con información pública y nunca vendemos tu información personal.';

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://alertalegal.co';
  const canonicalUrl = `${siteUrl}${t('routes.ourCommitment')}`;
  const structuredData = getTrustPageData(pageTitle, pageDescription, t('routes.ourCommitment'));

  const commitments = [
    {
      icon: <VisibilityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: t('ourCommitment.principles.publicData.title'),
      description: t('ourCommitment.principles.publicData.description'),
      details: [
        t('ourCommitment.principles.publicData.detail1'),
        t('ourCommitment.principles.publicData.detail2'),
        t('ourCommitment.principles.publicData.detail3'),
      ]
    },
    {
      icon: <ShieldIcon sx={{ fontSize: 40, color: 'success.main' }} />,
      title: t('ourCommitment.principles.noDataSale.title'),
      description: t('ourCommitment.principles.noDataSale.description'),
      details: [
        t('ourCommitment.principles.noDataSale.detail1'),
        t('ourCommitment.principles.noDataSale.detail2'),
        t('ourCommitment.principles.noDataSale.detail3'),
      ]
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: 'info.main' }} />,
      title: t('ourCommitment.principles.security.title'),
      description: t('ourCommitment.principles.security.description'),
      details: [
        t('ourCommitment.principles.security.detail1'),
        t('ourCommitment.principles.security.detail2'),
        t('ourCommitment.principles.security.detail3'),
      ]
    },
    {
      icon: <VerifiedUserIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
      title: t('ourCommitment.principles.userControl.title'),
      description: t('ourCommitment.principles.userControl.description'),
      details: [
        t('ourCommitment.principles.userControl.detail1'),
        t('ourCommitment.principles.userControl.detail2'),
        t('ourCommitment.principles.userControl.detail3'),
      ]
    },
  ];

  const securityMeasures = [
    t('ourCommitment.security.encryption'),
    t('ourCommitment.security.https'),
    t('ourCommitment.security.accessControls'),
    t('ourCommitment.security.regularAudits'),
    t('ourCommitment.security.dataBackups'),
    t('ourCommitment.security.monitoring'),
  ];

  return (
    <>
      <SEOErrorBoundary fallbackTitle="Our Commitment - AlertaLegal" fallbackDescription="Learn about Alerta Legal commitment to privacy and security.">
        <SEO 
          title={pageTitle}
          description={pageDescription}
          canonical={canonicalUrl}
          structuredData={structuredData}
          hreflangEnabled={true}
        />
      </SEOErrorBoundary>
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom 
            align="center"
            tabIndex={-1}
            id="main-heading"
            sx={{ 
              fontWeight: 600,
              color: 'primary.main',
              mb: 3,
              '&:focus': {
                outline: 'none'
              }
            }}
          >
            {t('ourCommitment.title')}
          </Typography>
          
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 6, 
              textAlign: 'center', 
              color: 'text.secondary',
              fontSize: '1.2rem',
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            {t('ourCommitment.subtitle')}
          </Typography>

          {/* Trust Badge */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              icon={<LockIcon />}
              label={t('ourCommitment.trustBadge')}
              variant="outlined"
              size="medium"
              sx={{
                px: 3,
                py: 1,
                fontSize: '1.1rem',
                fontWeight: 600,
                color: 'primary.main',
                borderColor: 'primary.main',
                borderWidth: 2,
              }}
            />
          </Box>

          {/* Introduction */}
          <Card sx={{ boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', mb: 6 }}>
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, textAlign: 'center' }}>
                {t('ourCommitment.introduction')}
              </Typography>
            </CardContent>
          </Card>

          {/* Core Commitments */}
          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              component="h2" 
              align="center"
              gutterBottom
              sx={{ 
                fontWeight: 600,
                color: 'primary.main',
                mb: 4 
              }}
            >
              {t('ourCommitment.principlesTitle')}
            </Typography>
            
            <Box sx={{ 
              display: 'grid', 
              gap: 4, 
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }
            }}>
              {commitments.map((commitment, index) => (
                <Box key={index}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 15px -3px rgba(0, 0, 0, 0.15)',
                      }
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Box sx={{ mr: 2 }}>
                          {commitment.icon}
                        </Box>
                        <Typography 
                          variant="h6" 
                          component="h3" 
                          sx={{ fontWeight: 600, color: 'primary.main' }}
                        >
                          {commitment.title}
                        </Typography>
                      </Box>
                      <Typography variant="body1" paragraph sx={{ color: 'text.secondary', lineHeight: 1.6, mb: 3 }}>
                        {commitment.description}
                      </Typography>
                      <List dense sx={{ pl: 0 }}>
                        {commitment.details.map((detail, detailIndex) => (
                          <ListItem key={detailIndex} sx={{ px: 0, py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary={detail}
                              primaryTypographyProps={{
                                variant: 'body2',
                                sx: { color: 'text.secondary' }
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 6 }} />

          {/* Security Measures */}
          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              component="h2" 
              align="center"
              gutterBottom
              sx={{ 
                fontWeight: 600,
                color: 'primary.main',
                mb: 4 
              }}
            >
              {t('ourCommitment.securityTitle')}
            </Typography>
            
            <Card sx={{ boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', maxWidth: '800px', mx: 'auto' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 4, textAlign: 'center' }}>
                  {t('ourCommitment.securityDescription')}
                </Typography>
                <Box sx={{ 
                  display: 'grid', 
                  gap: 2, 
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }
                }}>
                  {securityMeasures.map((measure, index) => (
                    <Box key={index}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CheckCircleIcon sx={{ fontSize: 24, color: 'success.main', mr: 2 }} />
                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                          {measure}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Legal Framework */}
          <Card 
            sx={{ 
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              mb: 6 
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <PolicyIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Typography 
                  variant="h5" 
                  component="h2" 
                  sx={{ fontWeight: 600, color: 'primary.main' }}
                >
                  {t('ourCommitment.legalFramework.title')}
                </Typography>
              </Box>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                {t('ourCommitment.legalFramework.description')}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
                <Typography 
                  component={Link}
                  to={getLanguageUrl('privacyPolicy')}
                  variant="body1"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  📋 {t('ourCommitment.links.privacyPolicy')}
                </Typography>
                <Typography 
                  component={Link}
                  to={getLanguageUrl('legalDisclaimer')}
                  variant="body1"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  ⚖️ {t('ourCommitment.links.legalDisclaimer')}
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          {/* Contact Section */}
          <Card 
            sx={{ 
              background: 'linear-gradient(135deg, #1a365d 0%, #2c5282 100%)',
              color: 'white',
              textAlign: 'center',
              boxShadow: '0 8px 16px -4px rgba(0, 0, 0, 0.2)'
            }}
          >
            <CardContent sx={{ p: { xs: 4, md: 6 } }}>
              <SupportIcon sx={{ fontSize: 48, mb: 2 }} />
              <Typography 
                variant="h5" 
                component="h2" 
                gutterBottom 
                sx={{ fontWeight: 600, mb: 3 }}
              >
                {t('ourCommitment.contact.title')}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontSize: '1.1rem', 
                  lineHeight: 1.6,
                  maxWidth: '600px',
                  mx: 'auto',
                  mb: 2
                }}
              >
                {t('ourCommitment.contact.description')}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontSize: '1.1rem', 
                  fontWeight: 500
                }}
              >
                📧 support@alertalegal.co
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </>
  );
};

export default OurCommitment;